/**
 * Shared building blocks for the Articles and Regulations pages.
 *
 * The Articles and Regulations sections of the site are near-mirror images of
 * each other — same layout chrome, same data/relevance pipeline, same card
 * grid — differing only in accent color, copy, route base, and the concrete
 * card/skeleton components used. Rather than maintain two copies of that
 * scaffolding, the generic pieces live here and are parameterized by a small
 * set of props (colors / labels / data / route names).
 *
 * Scope: this file houses the shared **index-page** primitives (section 1) and
 * **detail-page** primitives (section 2). Create-page shared primitives are
 * intended to be added here over time under the same parameterized philosophy.
 *
 * Naming note: the pre-existing `RegulationCard` / `RegulationHeaderCard` /
 * `RegulationMainContent` components are already shared by BOTH entities (the
 * "Regulation" prefix is legacy naming, not an entity scope). They are imported
 * where needed and intentionally NOT renamed.
 */
import React from "react";
import Head from "next/head";
import { Avatar, Box, Button, Chip, Collapse, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Paper, TextField, Typography } from "@mui/material";
import { alpha, styled, useTheme, createTheme, ThemeProvider } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import ImageIcon from "@mui/icons-material/Image";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { common, gray, green, purple } from "@/theme/colors";
import { TAG_COLORS } from "@/data/presetTags";
import { assetUrl } from "@/utils/asset";
import { useTagPreferences } from "@/hooks/useTagPreferences";
import { classifyItemTags } from "@/utils/tagClassifier";
import { doesRegionMatchPrefs } from "@/utils/localStore";
import RegulationMainContent from "@/components/Regulations/RegulationMainContent";
import SavedToggleButton from "@/components/Saved/SavedToggleButton";
import EditorToolbarDrawer from "@/components/Regulations/EditorToolbarDrawer";
import IndexPageHeader from "@/components/Importable/IndexPageHeader";
import { MAIN_CONTENT_ID } from "@/components/Importable/SkipLink";

// Re-exported so both create pages import the editor toolbar (and its action
// type) from this shared module.

// ─── Shared accent config ────────────────────────────────────────────────────

/** The two-tone accent used to theme an entity's index page. */

// ─── 1. SEO <head> ───────────────────────────────────────────────────────────
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * SEO/meta block for an index page. Pure markup parameterized by copy + URL.
 */
export function IndexPageHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  twitterTitle,
  twitterDescription,
  jsonLd = null
}) {
  return /*#__PURE__*/_jsxs(Head, {
    children: [/*#__PURE__*/_jsx("title", {
      children: title
    }), /*#__PURE__*/_jsx("meta", {
      name: "description",
      content: description
    }), /*#__PURE__*/_jsx("meta", {
      name: "keywords",
      content: keywords
    }), /*#__PURE__*/_jsx("meta", {
      property: "og:title",
      content: ogTitle ?? title
    }), /*#__PURE__*/_jsx("meta", {
      property: "og:description",
      content: ogDescription ?? description
    }), /*#__PURE__*/_jsx("meta", {
      property: "og:type",
      content: "website"
    }), /*#__PURE__*/_jsx("meta", {
      property: "og:url",
      content: canonicalUrl
    }), /*#__PURE__*/_jsx("meta", {
      name: "twitter:card",
      content: "summary_large_image"
    }), /*#__PURE__*/_jsx("meta", {
      name: "twitter:title",
      content: twitterTitle ?? title
    }), /*#__PURE__*/_jsx("meta", {
      name: "twitter:description",
      content: twitterDescription ?? description
    }), /*#__PURE__*/_jsx("link", {
      rel: "canonical",
      href: canonicalUrl
    }), jsonLd && /*#__PURE__*/_jsx("script", {
      type: "application/ld+json",
      children: JSON.stringify(jsonLd)
    })]
  });
}

// ─── 2. Layout shell ─────────────────────────────────────────────────────────

/**
 * The full-bleed tinted background + left-sidebar-aware content wrapper +
 * responsive container + white "paper" card that every index page renders its
 * header and grid inside. Only the accent tint and a11y labels differ between
 * entities. Sidenav / NavBar / banner / footer are rendered by the page around
 * this shell (they sit outside the tinted MainContainer).
 */
export function IndexLayoutShell({
  accent,
  ariaLabel,
  dataSelectTarget,
  topOffset,
  hasSideNav = true,
  style = 2,
  children
}) {
  // Only the panel look tints the page and floats a white card; the
  // other two sit on the same neutral wash the organizations page uses.
  const tinted = style === 2;
  return /*#__PURE__*/_jsx(Box, {
    sx: {
      display: "flex",
      minHeight: "100vh",
      overflowX: "hidden",
      width: "100%",
      maxWidth: "100vw",
      backgroundColor: tinted ? alpha(accent.primary, 0.1) : alpha(gray.light, 0.1),
      ...(topOffset !== undefined ? {
        pt: topOffset
      } : {})
    },
    children: /*#__PURE__*/_jsx(Box, {
      sx: {
        flexGrow: 1,
        ml: 0,
        overflowX: "hidden",
        width: "100%",
        maxWidth: hasSideNav ? {
          xs: "100%",
          lg: "calc(100% - 320px)"
        } : "100%",
        marginLeft: hasSideNav ? {
          lg: "320px"
        } : 0,
        transition: theme => theme.transitions.create("margin", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen
        })
      },
      children: /*#__PURE__*/_jsx(Container
      // Directory style matches the organizations page exactly:
      // maxWidth="lg" with even spacing(4) padding. The panel
      // style keeps the wider asymmetric padding that clears
      // the sidebar pull-tab. The banner style fills the
      // column (like the events page at style 3) so the hero's
      // container bleed reaches the true column edges instead
      // of a centered lg box.
      , {
        maxWidth: tinted ? "xl" : style === 3 ? false : "lg",
        sx: tinted ? {
          py: 4,
          px: {
            xs: 4,
            sm: 6,
            md: 8,
            lg: 10
          },
          "@media (max-width:375px)": {
            pl: 3,
            pr: 3
          }
        } : style === 3 ? {
          // Banner style: the hero must sit flush
          // against the navbar, so no top padding
          // here — the hero supplies its own
          // breathing room and the content below
          // gets its gap from the wrapper's pt.
          pt: 0,
          pb: 4,
          px: 4
        } : {
          py: 4,
          px: 4
        },
        children: /*#__PURE__*/_jsx(Box, {
          component: "main",
          id: MAIN_CONTENT_ID,
          tabIndex: -1,
          "aria-label": ariaLabel,
          "data-select-target": dataSelectTarget,
          sx: {
            minHeight: 300,
            p: {
              xs: 2.5,
              md: 3,
              lg: 4
            },
            "@media (max-width:375px)": {
              p: 2
            },
            // Banner style: the hero sits flush to the
            // navbar (its own mb spaces the content below),
            // so no top padding here.
            ...(style === 3 ? {
              pt: 0
            } : {}),
            ...(tinted ? {
              backgroundColor: "background.paper",
              borderRadius: 1,
              boxShadow: 1,
              my: 3
            } : {
              // Directory/banner: content sits
              // directly on the page, no card.
              p: 0,
              "@media (max-width:375px)": {
                p: 0
              }
            })
          },
          children: children
        })
      })
    })
  });
}

// ─── 3. List header (title + Create chip + intro) ────────────────────────────

/**
 * The page heading with an inline "Create X" chip, plus the intro paragraph.
 */
export function IndexListHeader({
  title,
  createLabel,
  onCreate,
  accent,
  description,
  style = 2,
  showCreate = true,
  ariaLabel,
  hasFeatured = false
}) {
  // Any style other than this shell's native one delegates to the
  // shared header, which owns all three looks.
  if (style !== 2 || !showCreate) {
    return /*#__PURE__*/_jsx(IndexPageHeader, {
      style: style,
      accent: {
        primary: accent.primary,
        dark: accent.dark
      },
      title: title,
      subtitle: description,
      ariaLabel: ariaLabel ?? `${title} Header`,
      create: showCreate ? {
        label: createLabel,
        onClick: onCreate
      } : undefined,
      hasFeatured: hasFeatured
      // This header lives inside the shell's side-nav-gutted
      // column: viewport bleed would shift the hero right by half
      // the gutter and clip it (see IndexPageHeaderProps.bleed).
      ,
      bleed: "container"
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Typography, {
      variant: "h4",
      gutterBottom: true,
      sx: {
        fontWeight: 600,
        color: accent.primary,
        mb: 3
      },
      children: /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap"
        },
        children: [/*#__PURE__*/_jsx(Box, {
          component: "span",
          children: title
        }), /*#__PURE__*/_jsx(Chip, {
          label: createLabel,
          onClick: onCreate,
          sx: {
            cursor: "pointer",
            fontWeight: 600,
            bgcolor: accent.primary,
            color: common.white,
            "&:hover": {
              bgcolor: accent.dark
            }
          }
        })]
      })
    }), /*#__PURE__*/_jsx(Typography, {
      variant: "body1",
      paragraph: true,
      sx: {
        mb: 4
      },
      children: description
    })]
  });
}

// ─── 4. Relevance split hook ─────────────────────────────────────────────────

/**
 * Shared "Most relevant to you" partitioning used by both index pages. Reads
 * the user's tag preferences and splits the already-visible items into a
 * relevant set (any tag matches a pref) and a remaining set, plus a helper that
 * returns the subset of an item's tags that match the user's prefs (for chip
 * highlighting). Identical logic previously duplicated in both pages.
 */
export function useRelevanceSplit(visibleItems) {
  const {
    prefs
  } = useTagPreferences();
  const getHighlightedTags = React.useCallback(tags => tags.filter(tag => {
    const t = tag.toLowerCase();
    return prefs.disciplines.some(d => d.toLowerCase() === t) || prefs.products.some(p => p.toLowerCase() === t) || prefs.regions.some(r => r.toLowerCase() === t) || doesRegionMatchPrefs(tag, prefs.regions);
  }), [prefs]);
  const split = React.useMemo(() => {
    const hasPrefs = prefs.disciplines.length > 0 || prefs.products.length > 0 || prefs.regions.length > 0;
    if (!hasPrefs || visibleItems.length === 0) {
      return {
        relevant: [],
        remaining: [],
        showRelevanceSection: false
      };
    }
    const prefDisc = new Set(prefs.disciplines);
    const prefProd = new Set(prefs.products);
    const prefReg = new Set(prefs.regions);
    const scored = visibleItems.map((item, idx) => {
      const c = classifyItemTags(item);
      let s = 0;
      c.disciplines.forEach(d => {
        if (prefDisc.has(d)) s += 1;
      });
      c.products.forEach(p => {
        if (prefProd.has(p)) s += 1;
      });
      c.regions.forEach(r => {
        if (prefReg.has(r)) s += 1;
      });
      return {
        item,
        score: s,
        idx
      };
    });
    const relevant = scored.filter(s => s.score > 0).sort((a, b) => b.score - a.score || a.idx - b.idx).map(s => s.item);
    const remaining = scored.filter(s => s.score === 0).map(s => s.item);
    return {
      relevant,
      remaining,
      showRelevanceSection: relevant.length > 0 && remaining.length > 0
    };
  }, [visibleItems, prefs]);
  return {
    ...split,
    getHighlightedTags
  };
}

// ─── 5. Results grid (skeletons + relevance split + remaining divider) ───────

const GRID_SX = {
  display: "flex",
  flexWrap: "wrap",
  columnGap: {
    xs: 2,
    md: 2.5
  },
  rowGap: {
    xs: 2.5,
    md: 3
  },
  justifyContent: {
    xs: "flex-start",
    md: "center"
  },
  width: "100%",
  mx: "auto"
};

/**
 * List view: a single column of full-width rows. The card components size
 * themselves, so width is forced from the container — keeping the layout
 * switch out of every individual card.
 */
const LIST_SX = {
  display: "flex",
  flexDirection: "column",
  rowGap: {
    xs: 2,
    md: 2.5
  },
  width: "100%",
  mx: "auto",
  "& > *": {
    width: "100%",
    maxWidth: "100%"
  },
  "& .MuiCard-root": {
    width: "100%",
    maxWidth: "100%"
  }
};
/**
 * The flex-wrap card grid shared by both index pages: optional skeletons, an
 * optional leading slot (draft card), a "Most relevant to you" heading + the
 * relevant cards, then a "Remaining results" divider + the remaining cards.
 * When `showRelevanceSection` is false it simply renders `fallbackItems`.
 */
export function IndexResultsGrid({
  showRelevanceSection,
  relevantItems,
  remainingItems,
  fallbackItems,
  renderCard,
  getKey,
  showSkeletons = false,
  skeletonCount = 0,
  renderSkeleton,
  leadingSlot,
  relevanceHeading = "Most relevant to you",
  remainingLabel = "Remaining results",
  viewMode = "grid"
}) {
  const primaryItems = showRelevanceSection ? relevantItems : fallbackItems;
  return /*#__PURE__*/_jsxs(Box, {
    sx: viewMode === "list" ? LIST_SX : GRID_SX,
    children: [showSkeletons && renderSkeleton && Array.from({
      length: skeletonCount
    }).map((_, i) => renderSkeleton(i)), leadingSlot, showRelevanceSection && /*#__PURE__*/_jsx(Typography, {
      variant: "h6",
      sx: {
        fontWeight: 600,
        width: "100%",
        mt: 1,
        mb: 2
      },
      children: relevanceHeading
    }), primaryItems.map((item, index) => /*#__PURE__*/_jsx(React.Fragment, {
      children: renderCard(item, index)
    }, getKey(item, index))), showRelevanceSection && remainingItems.length > 0 && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Divider, {
        sx: {
          width: "100%",
          my: 4
        },
        children: /*#__PURE__*/_jsx(Typography, {
          variant: "body2",
          color: "text.secondary",
          children: remainingLabel
        })
      }), remainingItems.map((item, index) => /*#__PURE__*/_jsx(React.Fragment, {
        children: renderCard(item, index)
      }, `rem-${getKey(item, index)}`))]
    })]
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Section 2 — Detail-page primitives
// ═══════════════════════════════════════════════════════════════════════════

const COMPANY = "Your Company";

// ─── SEO <head> ──────────────────────────────────────────────────────────────

/** SEO/meta block for a detail page (Article/Regulation), parameterized by copy + URL. */
export function DetailPageHead({
  title,
  description,
  keywords,
  url,
  jsonLd = null
}) {
  const fullTitle = `${title} | ${COMPANY}`;
  return /*#__PURE__*/_jsxs(Head, {
    children: [/*#__PURE__*/_jsx("title", {
      children: fullTitle
    }), /*#__PURE__*/_jsx("meta", {
      name: "description",
      content: description
    }), /*#__PURE__*/_jsx("meta", {
      name: "keywords",
      content: keywords
    }), /*#__PURE__*/_jsx("meta", {
      property: "og:title",
      content: fullTitle
    }), /*#__PURE__*/_jsx("meta", {
      property: "og:description",
      content: description
    }), /*#__PURE__*/_jsx("meta", {
      property: "og:type",
      content: "article"
    }), /*#__PURE__*/_jsx("meta", {
      property: "og:url",
      content: url
    }), /*#__PURE__*/_jsx("meta", {
      name: "twitter:card",
      content: "summary_large_image"
    }), /*#__PURE__*/_jsx("meta", {
      name: "twitter:title",
      content: fullTitle
    }), /*#__PURE__*/_jsx("meta", {
      name: "twitter:description",
      content: description
    }), /*#__PURE__*/_jsx("link", {
      rel: "canonical",
      href: url
    }), jsonLd && /*#__PURE__*/_jsx("script", {
      type: "application/ld+json",
      children: JSON.stringify(jsonLd)
    })]
  });
}

// ─── Not-found state ─────────────────────────────────────────────────────────

/** Centered "{noun} Not Found" placeholder. */
export function DetailNotFound({
  noun,
  backHref,
  backLabel,
  accentColor
}) {
  return /*#__PURE__*/_jsxs(Box, {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    flexDirection: "column",
    children: [/*#__PURE__*/_jsxs(Typography, {
      variant: "h4",
      gutterBottom: true,
      children: [noun, " Not Found"]
    }), backHref && accentColor && /*#__PURE__*/_jsx(Button, {
      href: backHref,
      variant: "outlined",
      sx: {
        mt: 1,
        color: accentColor.primary,
        borderColor: accentColor.primary,
        "&:hover": {
          borderColor: accentColor.dark,
          backgroundColor: alpha(accentColor.primary, 0.08)
        }
      },
      children: backLabel ?? `Back to ${noun}s`
    })]
  });
}

// ─── Type badge ──────────────────────────────────────────────────────────────

/** The accent-bordered "type tab" that overlaps the top edge of a header card. */
export function DetailTypeBadge({
  label,
  color
}) {
  return /*#__PURE__*/_jsx(Box, {
    sx: {
      position: "absolute",
      top: 0,
      left: 2,
      transform: "translateY(-100%)",
      zIndex: 2,
      backgroundColor: "background.paper",
      marginLeft: 0.75
    },
    children: /*#__PURE__*/_jsx(Box, {
      component: "span",
      sx: {
        display: "inline-flex",
        alignItems: "center",
        marginTop: "-2px",
        px: 1.5,
        py: 0.5,
        border: `2px solid ${color}`,
        borderBottom: "none",
        color,
        borderRadius: theme => `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
        fontWeight: 700,
        fontSize: "0.75rem",
        backgroundColor: "background.paper"
      },
      children: label
    })
  });
}

// ─── Author / attributed-organization card ───────────────────────────────────

/**
 * Compact attributed-organization badge — the org block from
 * DetailAuthorOrgCard without the "Visit Page" button: the entire badge is
 * clickable and opens the organization page in a new tab. Used on
 * professional profiles; regulations/articles keep the full author card.
 */
export function AttributedOrgBadge({
  name,
  logoUrl,
  href,
  accentColor = green.primary
}) {
  return /*#__PURE__*/_jsxs(Paper, {
    variant: "outlined",
    role: "link",
    "aria-label": `Open ${name} in a new tab`,
    onClick: () => window.open(href, "_blank", "noopener,noreferrer"),
    sx: {
      p: 1.5,
      borderColor: accentColor,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      cursor: "pointer",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: `${accentColor}14`,
        boxShadow: 2
      }
    },
    children: [logoUrl ? /*#__PURE__*/_jsx(Avatar, {
      src: logoUrl,
      alt: name,
      sx: {
        width: 48,
        height: 48
      }
    }) : /*#__PURE__*/_jsx(Avatar, {
      sx: {
        bgcolor: accentColor,
        width: 48,
        height: 48
      },
      children: (name || "O").charAt(0).toUpperCase()
    }), /*#__PURE__*/_jsxs(Box, {
      sx: {
        minWidth: 0
      },
      children: [/*#__PURE__*/_jsx(Typography, {
        variant: "subtitle2",
        color: accentColor,
        sx: {
          lineHeight: 1.2
        },
        children: "Attributed Organization"
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "body1",
        fontWeight: 700,
        sx: {
          wordBreak: "break-word"
        },
        children: name
      })]
    })]
  });
}
/**
 * Right-column card on a detail header: optional attributed-organization block
 * (logo, name, "Visit Page"), then the author block (avatar, name, email),
 * published date, and an optional "Last Updated" link. Shared verbatim between
 * articles and regulations; entity-specific values are resolved by the caller.
 */
export function DetailAuthorOrgCard({
  accentColor = green.primary,
  org = null,
  orgInitialFallback = false,
  authorLabel,
  author,
  authorInitialFallback = false,
  publishedText,
  lastUpdatedText,
  onLastUpdatedClick
}) {
  const showLastUpdated = !!lastUpdatedText && lastUpdatedText !== publishedText;
  return /*#__PURE__*/_jsxs(Paper, {
    variant: "outlined",
    sx: {
      p: 2,
      borderColor: accentColor,
      justifySelf: {
        xs: "start",
        md: "end"
      },
      // Keep this card's border above the Executive Summary toggle's
      // white background pill so it isn't painted over.
      position: "relative",
      zIndex: 2
    },
    children: [org && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 1.5
        },
        children: [org.logoUrl ? /*#__PURE__*/_jsx(Avatar, {
          src: org.logoUrl,
          alt: org.name || "Org",
          sx: {
            width: 48,
            height: 48
          }
        }) : orgInitialFallback ? /*#__PURE__*/_jsx(Avatar, {
          sx: {
            bgcolor: accentColor,
            width: 48,
            height: 48
          },
          children: (org.name || "O").charAt(0).toUpperCase()
        }) : null, /*#__PURE__*/_jsxs(Box, {
          sx: {
            minWidth: 0,
            flex: 1
          },
          children: [/*#__PURE__*/_jsx(Typography, {
            variant: "subtitle2",
            color: accentColor,
            sx: {
              lineHeight: 1.2
            },
            children: "Attributed Organization"
          }), /*#__PURE__*/_jsx(Typography, {
            variant: "body1",
            fontWeight: 700,
            sx: {
              wordBreak: "break-word"
            },
            children: org.name
          })]
        })]
      }), /*#__PURE__*/_jsx(Button, {
        variant: "outlined",
        size: "small",
        fullWidth: true,
        sx: {
          mb: 2,
          borderColor: accentColor,
          color: accentColor,
          "&:hover": {
            borderColor: accentColor,
            backgroundColor: `${accentColor}14`
          }
        },
        onClick: org.onVisit,
        children: "Visit Page"
      }), /*#__PURE__*/_jsx(Divider, {
        sx: {
          mb: 2
        }
      })]
    }), /*#__PURE__*/_jsx(Typography, {
      variant: "subtitle2",
      color: accentColor,
      gutterBottom: true,
      children: authorLabel
    }), /*#__PURE__*/_jsxs(Box, {
      sx: {
        display: "flex",
        alignItems: "center",
        gap: 2,
        mb: 1
      },
      children: [author.photoUrl ? /*#__PURE__*/_jsx(Avatar, {
        src: author.photoUrl,
        alt: author.name,
        imgProps: {
          onError: event => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = "";
          }
        },
        children: (author.name || "?").trim().charAt(0).toUpperCase()
      }) : authorInitialFallback ? /*#__PURE__*/_jsx(Avatar, {
        children: (author.name || "?").trim().charAt(0).toUpperCase()
      }) : null, /*#__PURE__*/_jsxs(Box, {
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "body2",
          fontWeight: 600,
          children: author.name
        }), author.email && /*#__PURE__*/_jsx(Typography, {
          variant: "caption",
          color: "text.secondary",
          children: author.email
        })]
      })]
    }), /*#__PURE__*/_jsxs(Typography, {
      variant: "body2",
      color: "text.secondary",
      children: ["Published: ", publishedText]
    }), showLastUpdated && /*#__PURE__*/_jsxs(Typography, {
      variant: "body2",
      color: "text.secondary",
      children: ["Last Updated: ", /*#__PURE__*/_jsx("a", {
        href: "#",
        onClick: e => {
          e.preventDefault();
          onLastUpdatedClick?.();
        },
        children: lastUpdatedText
      })]
    })]
  });
}

// ─── Published body content ──────────────────────────────────────────────────

/**
 * The published article/regulation body: a width-constrained wrapper around the
 * shared `RegulationMainContent` renderer. `html` should already be
 * auto-formatted by the caller; `accentColor` themes the rendered content.
 */
export function DetailBodyContent({
  html,
  accentColor,
  ariaLabel,
  dataSelectTarget
}) {
  return /*#__PURE__*/_jsx(Box, {
    "aria-label": ariaLabel,
    "data-select-target": dataSelectTarget,
    sx: {
      maxWidth: "1200px",
      margin: "15px auto",
      overflowWrap: "break-word",
      wordBreak: "break-word",
      hyphens: "auto",
      lineHeight: 1.6,
      backgroundColor: "background.default",
      "& h1, & h2, & h3, & h4, & h5, & h6": {
        mt: 3,
        mb: 2,
        fontWeight: 600
      },
      "& p": {
        mb: 2
      },
      "@media (max-width:900px)": {
        maxWidth: "none",
        margin: "15px 0"
      }
    },
    children: /*#__PURE__*/_jsx(RegulationMainContent, {
      html: html,
      accentColor: accentColor
    })
  });
}

// ─── Deactivation request dialog ─────────────────────────────────────────────

/**
 * Admin/author deactivation-request dialog: explanatory copy, a thread of prior
 * requests (status chip + admin/author messages), and a required reason field.
 * Identical between entities apart from the noun and the submit handler.
 */
export function DeactivateRequestDialog({
  open,
  onClose,
  noun,
  reason,
  onReasonChange,
  deactivationHistory,
  onSubmit
}) {
  return /*#__PURE__*/_jsxs(Dialog, {
    open: open,
    onClose: onClose,
    maxWidth: "sm",
    fullWidth: true,
    children: [/*#__PURE__*/_jsx(DialogTitle, {
      children: "Deactivate this page?"
    }), /*#__PURE__*/_jsxs(DialogContent, {
      children: [/*#__PURE__*/_jsxs(Typography, {
        variant: "body2",
        color: "text.secondary",
        sx: {
          mb: 2
        },
        children: ["This will submit a deactivation request to remove this ", noun, " ", "from public view."]
      }), deactivationHistory && deactivationHistory.length > 0 && /*#__PURE__*/_jsxs(Box, {
        sx: {
          mb: 2
        },
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "subtitle2",
          sx: {
            mb: 1
          },
          children: "Previous Deactivation Requests"
        }), deactivationHistory.map(entry => /*#__PURE__*/_jsxs(Paper, {
          variant: "outlined",
          sx: {
            p: 1.5,
            mb: 1
          },
          children: [/*#__PURE__*/_jsxs(Box, {
            sx: {
              display: "flex",
              justifyContent: "space-between",
              mb: 0.5
            },
            children: [/*#__PURE__*/_jsx(Chip, {
              label: entry.status,
              size: "small",
              color: entry.status === "REJECTED" ? "error" : entry.status === "APPROVED" ? "success" : "default",
              variant: "outlined"
            }), /*#__PURE__*/_jsx(Typography, {
              variant: "caption",
              color: "text.secondary",
              children: new Date(entry.createdAt).toLocaleString()
            })]
          }), entry.messages.map(msg => /*#__PURE__*/_jsxs(Box, {
            sx: {
              mt: 1,
              pl: 1,
              borderLeft: "3px solid",
              borderColor: msg.authorRole === "admin" ? "error.main" : "primary.main"
            },
            children: [/*#__PURE__*/_jsx(Typography, {
              variant: "caption",
              fontWeight: "bold",
              color: msg.authorRole === "admin" ? "error.main" : "primary.main",
              children: msg.authorRole === "admin" ? "Admin Response" : "Your Request"
            }), /*#__PURE__*/_jsx(Typography, {
              variant: "caption",
              color: "text.secondary",
              sx: {
                ml: 1
              },
              children: new Date(msg.createdAt).toLocaleString()
            }), /*#__PURE__*/_jsx(Typography, {
              variant: "body2",
              sx: {
                mt: 0.25
              },
              children: msg.body
            })]
          }, msg.id))]
        }, entry.submissionId)), /*#__PURE__*/_jsx(Divider, {
          sx: {
            my: 1.5
          }
        })]
      }), /*#__PURE__*/_jsx(TextField, {
        label: "Reason for deactivation request",
        multiline: true,
        minRows: 3,
        fullWidth: true,
        required: true,
        value: reason,
        onChange: e => onReasonChange(e.target.value),
        placeholder: "Explain why this page should be deactivated\u2026"
      })]
    }), /*#__PURE__*/_jsxs(DialogActions, {
      children: [/*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        children: "Cancel"
      }), /*#__PURE__*/_jsx(Button, {
        color: "error",
        variant: "contained",
        disabled: !reason.trim(),
        onClick: onSubmit,
        children: "Submit Deactivation Request"
      })]
    })]
  });
}

// ─── Floating edit / promote controls ────────────────────────────────────────

/**
 * Fixed bottom-right admin/author control stack: an expandable panel with
 * Deactivate, Promote/reorder/remove, and "Edit Page Contents". Identical
 * between entities; all behavior is injected via handlers.
 */
export function DetailFloatingEditPanel({
  editPanelOpen,
  onTogglePanel,
  showExpandedMenu,
  onEdit,
  deactivationEnabled,
  onDeactivate,
  promotionEnabled,
  canPromote = true,
  existingPromo,
  allPromotions,
  onPromote,
  onReorder,
  onDeletePromo,
  extraActions,
  bottomOffset = 20
}) {
  return /*#__PURE__*/_jsxs(Box, {
    sx: {
      position: "fixed",
      bottom: bottomOffset,
      right: 20,
      zIndex: t => t.zIndex.tooltip + 1,
      display: "flex",
      flexDirection: "column",
      gap: 1,
      alignItems: "flex-end"
    },
    children: [editPanelOpen && /*#__PURE__*/_jsxs(_Fragment, {
      children: [deactivationEnabled && /*#__PURE__*/_jsx(Button, {
        variant: "contained",
        color: "error",
        onClick: onDeactivate,
        children: "Deactivate this page"
      }), promotionEnabled && !existingPromo && canPromote && /*#__PURE__*/_jsx(Button, {
        variant: "contained",
        color: "warning",
        onClick: onPromote,
        children: "Promote Page"
      }), promotionEnabled && existingPromo && (() => {
        const samePlacement = allPromotions?.filter(p => p.placement === existingPromo.placement) ?? [];
        const idx = samePlacement.findIndex(p => p.id === existingPromo.id) + 1;
        const total = samePlacement.length;
        return /*#__PURE__*/_jsxs(Box, {
          sx: {
            display: "flex",
            gap: 0.5,
            alignItems: "center"
          },
          children: [total > 1 && /*#__PURE__*/_jsx(Button, {
            size: "small",
            variant: "contained",
            color: "warning",
            onClick: () => onReorder(existingPromo.id, "up"),
            children: "\u2191"
          }), /*#__PURE__*/_jsxs(Typography, {
            variant: "caption",
            sx: {
              fontWeight: 700,
              mx: 0.5
            },
            children: [idx, "/", total]
          }), total > 1 && /*#__PURE__*/_jsx(Button, {
            size: "small",
            variant: "contained",
            color: "warning",
            onClick: () => onReorder(existingPromo.id, "down"),
            children: "\u2193"
          }), /*#__PURE__*/_jsx(Button, {
            size: "small",
            variant: "contained",
            color: "error",
            onClick: () => onDeletePromo(existingPromo.id),
            children: "\u2715"
          })]
        });
      })(), extraActions, /*#__PURE__*/_jsx(Button, {
        variant: "contained",
        startIcon: /*#__PURE__*/_jsx(EditIcon, {}),
        onClick: onEdit,
        children: "Edit Page Contents"
      })]
    }), /*#__PURE__*/_jsx(Button, {
      variant: "contained",
      color: editPanelOpen ? "inherit" : "primary",
      onClick: () => {
        if (!showExpandedMenu) onEdit();else onTogglePanel();
      },
      children: editPanelOpen ? "Close" : "Edit Page Contents"
    })]
  });
}

/** Fixed bottom-right "Suggest an Edit" button for unauthenticated guests. */
/**
 * The org owner/manager floating menu ("Manage this Page"): Promote this
 * Page + Edit Page Contents. Rendered alone for non-admin owner/managers'
 * promote entry point, or stacked BELOW the admin panel when the account
 * is both a site admin and part of the attributed org's team.
 */
export function OrgTeamManagePanel({
  onPromote,
  onEdit,
  bottomOffset = 20,
  showEdit = true
}) {
  // Flat: the actions ARE the menu. The old "Manage this Page" opener
  // added a click to reach two buttons, one of which duplicated the
  // standalone edit button already on the page.
  return /*#__PURE__*/_jsxs(Box, {
    sx: {
      position: "fixed",
      bottom: bottomOffset,
      right: 20,
      zIndex: t => t.zIndex.tooltip + 1,
      display: "flex",
      flexDirection: "column",
      gap: 1,
      alignItems: "flex-end"
    },
    children: [/*#__PURE__*/_jsx(Button, {
      variant: "contained",
      color: "warning",
      onClick: onPromote,
      children: "Promote this Page"
    }), showEdit && /*#__PURE__*/_jsx(Button, {
      variant: "contained",
      startIcon: /*#__PURE__*/_jsx(EditIcon, {}),
      onClick: onEdit,
      children: "Edit Page Contents"
    })]
  });
}
export function GuestSuggestEditButton({
  onSuggest
}) {
  return /*#__PURE__*/_jsx(Box, {
    sx: {
      position: "fixed",
      bottom: 20,
      right: 20,
      zIndex: 1000
    },
    children: /*#__PURE__*/_jsx(Button, {
      variant: "contained",
      startIcon: /*#__PURE__*/_jsx(EditIcon, {}),
      onClick: onSuggest,
      children: "Suggest an Edit"
    })
  });
}

// ─── Detail header card (shared by Articles & Regulations) ───────────────────

const DetailHeaderSurface = styled(Box)(({
  theme
}) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(4, 4, 2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  border: `2px solid ${theme.palette.divider}`,
  position: "relative",
  overflowWrap: "break-word",
  wordBreak: "break-word",
  hyphens: "auto",
  maxWidth: "1200px",
  margin: `0 auto ${theme.spacing(4)}px auto`,
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3, 3, 2),
    margin: `0 auto ${theme.spacing(3)}px auto`,
    maxWidth: "none"
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    margin: `0 auto ${theme.spacing(2)}px auto`
  },
  "@media (max-width: 375px)": {
    padding: theme.spacing(1.5, 1.5, 2),
    margin: `0 auto ${theme.spacing(1.5)}px auto`
  }
}));
const DetailTagsRow = styled(Box)(({
  theme
}) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  "& .MuiChip-root": {
    fontSize: "0.75rem",
    height: "24px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.6875rem",
      height: "22px"
    },
    "@media (max-width: 375px)": {
      fontSize: "0.625rem",
      height: "20px"
    }
  }
}));
/**
 * The detail-page header card shared by both Articles and Regulations: an
 * accent-bordered surface with the type tab (purple "ARTICLE" / primary
 * "REGULATORY BRIEF" — derived from `item.regulationType`), title, top-right
 * Saved + Assets cluster, a color-coded chip meta-grid (category/tags =
 * primary, regions = purple, disciplines = green, products = blue), the shared
 * author/organization card, and a collapsible executive-summary drawer. The two
 * entities are identical except `resourceType`/`authorLabel`/`nounLabel`.
 */
export function DetailHeaderCard({
  item,
  resourceType,
  authorLabel,
  nounLabel,
  uniqueTags,
  uniqueRegions,
  selectedTags,
  setSelectedTags,
  selectedRegions,
  setSelectedRegions,
  imageAssets,
  onOpenAssets,
  org,
  formatDate,
  summaryOpen,
  onToggleSummary,
  onShowHistory,
  hideSideNav,
  compactPreview,
  followedDisciplines,
  followedProducts,
  onFollowTag
}) {
  const theme = useTheme();
  const accent = item.regulationType === "ARTICLE" ? purple.primary : theme.palette.primary.main;
  const disciplines = Array.isArray(item.disciplines) ? item.disciplines : [];
  const products = Array.isArray(item.products) ? item.products : [];
  return /*#__PURE__*/_jsxs(DetailHeaderSurface, {
    "data-select-target": `${nounLabel} Header`,
    "aria-label": `${nounLabel} Header`,
    sx: {
      borderColor: accent,
      boxShadow: "none"
    },
    children: [item.regulationType && /*#__PURE__*/_jsx(DetailTypeBadge, {
      label: item.regulationType,
      color: accent
    }), !hideSideNav && !compactPreview && /*#__PURE__*/_jsxs(Box, {
      sx: {
        position: "absolute",
        top: {
          xs: 8,
          sm: 12
        },
        right: {
          xs: 8,
          sm: 16
        },
        display: "flex",
        alignItems: "center",
        gap: 1,
        zIndex: 2
      },
      children: [/*#__PURE__*/_jsx(SavedToggleButton, {
        resourceType: resourceType,
        resourceId: item.id,
        resourceSlug: item.id,
        resourceTitle: item.title,
        resourceSummary: item.executiveSummary || item.shortTitle || undefined,
        resourceImage: item.author?.photoUrl ? assetUrl(item.author.photoUrl) : undefined,
        size: "small",
        variant: "outlined"
      }), imageAssets.length > 0 && /*#__PURE__*/_jsx(Button, {
        variant: "outlined",
        size: "small",
        startIcon: /*#__PURE__*/_jsx(ImageIcon, {}),
        onClick: onOpenAssets,
        sx: {
          borderColor: green.primary,
          color: green.primary,
          "&:hover": {
            borderColor: green.primary,
            backgroundColor: `${green.primary}14`
          }
        },
        children: "Assets"
      })]
    }), /*#__PURE__*/_jsx(Typography, {
      variant: "h3",
      component: "h1",
      gutterBottom: true,
      sx: {
        fontWeight: 700,
        color: theme.palette.primary.main,
        fontSize: {
          xs: "1.75rem",
          sm: "2.125rem",
          md: "2.5rem"
        },
        lineHeight: {
          xs: 1.2,
          sm: 1.3,
          md: 1.4
        },
        pt: "10px"
      },
      children: item.title
    }), /*#__PURE__*/_jsxs(Box, {
      sx: {
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "1fr auto"
        },
        alignItems: "start",
        gap: 2,
        mt: 1
      },
      children: [/*#__PURE__*/_jsxs(Box, {
        sx: {
          position: "relative",
          zIndex: 2
        },
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "subtitle1",
          color: "text.secondary",
          gutterBottom: true,
          sx: {
            fontSize: {
              xs: "0.9rem",
              sm: "1rem"
            },
            lineHeight: 1.4
          },
          children: item.shortTitle
        }), item.priority && /*#__PURE__*/_jsxs(Typography, {
          variant: "subtitle2",
          color: "primary",
          sx: {
            mb: 1
          },
          children: ["Priority: ", item.priority]
        }), /*#__PURE__*/_jsx(DetailTagsRow, {
          children: uniqueTags.map(tag => {
            const selected = selectedTags.has(tag);
            return /*#__PURE__*/_jsx(Chip, {
              label: tag,
              clickable: true,
              onClick: () => setSelectedTags(prev => {
                const next = new Set(prev);
                if (next.has(tag)) next.delete(tag);else next.add(tag);
                return next;
              }),
              variant: selected ? "filled" : "outlined",
              color: "primary",
              size: "small"
            }, tag);
          })
        }), /*#__PURE__*/_jsx(Box, {
          sx: {
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mt: 1
          },
          children: uniqueRegions.map(region => {
            const selected = selectedRegions.has(region);
            return /*#__PURE__*/_jsx(Chip, {
              label: region,
              clickable: true,
              onClick: () => setSelectedRegions(prev => {
                const next = new Set(prev);
                if (next.has(region)) next.delete(region);else next.add(region);
                return next;
              }),
              variant: selected ? "filled" : "outlined",
              size: "small",
              sx: {
                borderColor: purple.primary,
                color: selected ? purple.contrast : purple.primary,
                bgcolor: selected ? purple.primary : "transparent",
                "& .MuiChip-label": {
                  px: 1
                },
                "&:hover": {
                  bgcolor: selected ? purple.dark : purple.contrast
                }
              }
            }, region);
          })
        }), disciplines.length > 0 && /*#__PURE__*/_jsx(Box, {
          sx: {
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mt: 1
          },
          children: disciplines.map(tag => {
            const followed = !!followedDisciplines?.includes(tag);
            return /*#__PURE__*/_jsx(Chip, {
              label: tag,
              size: "small",
              variant: followed ? "filled" : "outlined",
              onClick: onFollowTag ? () => onFollowTag("disciplines", tag) : undefined,
              sx: {
                borderColor: TAG_COLORS.discipline.primary,
                color: followed ? "#fff" : TAG_COLORS.discipline.primary,
                bgcolor: followed ? TAG_COLORS.discipline.primary : "transparent",
                cursor: onFollowTag ? "pointer" : undefined,
                "& .MuiChip-label": {
                  px: 1
                },
                "&:hover": onFollowTag ? {
                  bgcolor: followed ? TAG_COLORS.discipline.dark : "transparent"
                } : undefined
              }
            }, `disc-${tag}`);
          })
        }), products.length > 0 && /*#__PURE__*/_jsx(Box, {
          sx: {
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mt: 1
          },
          children: products.map(tag => {
            const followed = !!followedProducts?.includes(tag);
            return /*#__PURE__*/_jsx(Chip, {
              label: tag,
              size: "small",
              variant: followed ? "filled" : "outlined",
              onClick: onFollowTag ? () => onFollowTag("products", tag) : undefined,
              sx: {
                borderColor: TAG_COLORS.product.primary,
                color: followed ? "#fff" : TAG_COLORS.product.primary,
                bgcolor: followed ? TAG_COLORS.product.primary : "transparent",
                cursor: onFollowTag ? "pointer" : undefined,
                "& .MuiChip-label": {
                  px: 1
                },
                "&:hover": onFollowTag ? {
                  bgcolor: followed ? TAG_COLORS.product.dark : "transparent"
                } : undefined
              }
            }, `prod-${tag}`);
          })
        })]
      }), item.author && /*#__PURE__*/_jsx(DetailAuthorOrgCard, {
        org: org,
        orgInitialFallback: true,
        authorInitialFallback: true,
        authorLabel: authorLabel,
        author: {
          name: item.author.name,
          email: item.author.email,
          photoUrl: item.author.photoUrl ? assetUrl(item.author.photoUrl) : undefined
        },
        publishedText: formatDate(item.publishDate),
        lastUpdatedText: item.publishDate !== item.lastUpdated ? formatDate(item.lastUpdated) : null,
        onLastUpdatedClick: onShowHistory
      })]
    }), item.executiveSummary && /*#__PURE__*/_jsxs(Paper, {
      variant: "outlined",
      "data-select-target": `${nounLabel} Summary`,
      "aria-label": `${nounLabel} Summary`,
      sx: {
        // When collapsed the Paper is zero-height and the toggle
        // chip is centered on its top edge; nudge it down so the
        // chip clears the header card's bottom border.
        mt: summaryOpen ? "13px" : "14px",
        position: "relative",
        pt: summaryOpen ? 4 : 0,
        p: summaryOpen ? 2 : 0,
        minHeight: summaryOpen ? undefined : 0,
        border: summaryOpen ? undefined : "none"
      },
      children: [/*#__PURE__*/_jsxs(Box, {
        role: "button",
        "aria-expanded": summaryOpen,
        onClick: onToggleSummary,
        sx: {
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          userSelect: "none",
          backgroundColor: theme.palette.background.paper,
          px: 1,
          borderRadius: 1
        },
        children: [/*#__PURE__*/_jsx(IconButton, {
          size: "small",
          "aria-label": "toggle executive summary",
          sx: {
            transform: summaryOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 200ms ease"
          },
          children: /*#__PURE__*/_jsx(ExpandMoreIcon, {})
        }), /*#__PURE__*/_jsx(Typography, {
          sx: {
            mb: 0
          },
          variant: "subtitle1",
          fontWeight: 700,
          children: "EXECUTIVE SUMMARY"
        }), /*#__PURE__*/_jsx(IconButton, {
          size: "small",
          "aria-label": "toggle executive summary",
          sx: {
            transform: summaryOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 200ms ease"
          },
          children: /*#__PURE__*/_jsx(ExpandMoreIcon, {})
        })]
      }), /*#__PURE__*/_jsx(Collapse, {
        in: summaryOpen,
        timeout: 350,
        children: /*#__PURE__*/_jsx(Typography, {
          variant: "body2",
          children: item.executiveSummary
        })
      })]
    })]
  });
}

// ─── Create-page tag input group (shared by both create pages) ───────────────

/**
 * One labeled tag group for the create pages: in preview mode it shows the
 * selected tags (filled when they match the user's preferences); in edit mode
 * it shows every preset tag as a toggle chip. Shared verbatim by the Articles
 * and Regulations create pages — only label/color/preset-list/selection differ.
 */
export function CreateTagInputGroup({
  label,
  color,
  presetTags,
  selectedTags,
  onToggle,
  matchedPrefs,
  previewMode
}) {
  return /*#__PURE__*/_jsxs(Box, {
    children: [!previewMode && /*#__PURE__*/_jsx(Typography, {
      variant: "subtitle2",
      sx: {
        fontWeight: 600,
        mb: 1,
        color
      },
      children: label
    }), previewMode ? /*#__PURE__*/_jsx(Box, {
      sx: {
        display: "flex",
        flexWrap: "wrap",
        gap: 0.75
      },
      children: selectedTags.length ? selectedTags.map(tag => {
        const matched = matchedPrefs.some(r => r.toLowerCase() === tag.toLowerCase());
        return /*#__PURE__*/_jsx(Chip, {
          label: tag,
          size: "small",
          variant: matched ? "filled" : "outlined",
          sx: {
            borderColor: color,
            color: matched ? common.white : color,
            bgcolor: matched ? color : "transparent"
          }
        }, tag);
      }) : /*#__PURE__*/_jsx(Typography, {
        variant: "caption",
        color: "text.secondary",
        children: "(None)"
      })
    }) : /*#__PURE__*/_jsx(Box, {
      sx: {
        display: "flex",
        flexWrap: "wrap",
        gap: 0.75
      },
      children: [...presetTags].map(tag => {
        const active = selectedTags.includes(tag);
        return /*#__PURE__*/_jsx(Chip, {
          label: tag,
          size: "small",
          clickable: true,
          onClick: () => onToggle(tag),
          variant: "outlined",
          sx: {
            borderColor: color,
            color: active ? common.white : color,
            bgcolor: active ? color : common.white,
            "&.MuiChip-clickable:hover": {
              bgcolor: active ? color : common.white
            },
            cursor: "pointer"
          }
        }, tag);
      })
    })]
  });
}

/**
 * The "type tab" badge that sits at the top-left of a create-page header card —
 * "ARTICLE" (purple) for articles, "REGULATORY BRIEF" (primary) for
 * regulations. Shared by both create pages.
 */
export function CreateEntityBadge({
  label,
  color
}) {
  return /*#__PURE__*/_jsx(Box, {
    sx: {
      position: "absolute",
      top: -10,
      left: 0,
      zIndex: 10
    },
    children: /*#__PURE__*/_jsx(Box, {
      sx: {
        bgcolor: "common.white",
        p: "5px",
        display: "inline-block",
        borderRadius: 1,
        zIndex: 10
      },
      children: /*#__PURE__*/_jsx(Box, {
        component: "span",
        sx: {
          display: "inline-block",
          px: 1.5,
          py: 0.5,
          border: `2px solid ${color}`,
          color,
          borderRadius: 1,
          fontWeight: 700,
          fontSize: "0.75rem",
          background: common.white
        },
        children: label
      })
    })
  });
}

// ─── Create-page editor toolbar (shared by both create pages) ────────────────

const TOOLBAR_ALLOWED_CONTROLS = {
  formatting: true,
  link: true,
  undoRedo: true,
  clear: true
};
const TOOLBAR_ALLOWED_INLINE = ["bold", "italic", "underline", "strike"];

/**
 * The rich-text editor toolbar drawer used by both create pages. Wraps the
 * shared `EditorToolbarDrawer` with the common `allowedControls` / `allowedInline`
 * config baked in so the Articles and Regulations editors expose the exact same
 * button set (including the image/asset controls). All page-specific handlers
 * are forwarded through; pass `accentColor` to theme it (purple for articles).
 */
export function CreateEditorToolbar(props) {
  const baseTheme = useTheme();
  // When an accent is supplied (purple for Articles), override the theme's
  // `primary` for the toolbar so EVERY primary-colored button (image-row,
  // image-with-text, table, presets, etc.) picks up the accent — not just the
  // ones that already read `accentColor`. The color-picker trigger buttons use
  // a neutral/inherit color, so they're unaffected.
  const themed = React.useMemo(() => props.accentColor ? createTheme(baseTheme, {
    palette: {
      primary: {
        main: props.accentColor
      }
    }
  }) : baseTheme, [baseTheme, props.accentColor]);
  const toolbar = /*#__PURE__*/_jsx(EditorToolbarDrawer, {
    allowedControls: TOOLBAR_ALLOWED_CONTROLS,
    allowedInline: TOOLBAR_ALLOWED_INLINE,
    ...props
  });
  return props.accentColor ? /*#__PURE__*/_jsx(ThemeProvider, {
    theme: themed,
    children: toolbar
  }) : toolbar;
}

// ─── Create-page executive summary (shared by both create pages) ─────────────

/**
 * The executive-summary section of a create page: a labeled multiline field in
 * edit mode, and a centered collapsible "EXECUTIVE SUMMARY" drawer in preview
 * mode. Identical for Articles and Regulations, so it lives here. (Uses the
 * roomier `mt: 3` spacing — the canonical version.)
 */
export function CreateExecutiveSummary({
  value,
  onChange,
  previewMode,
  open,
  onToggle
}) {
  return /*#__PURE__*/_jsx(Box, {
    mt: 3,
    children: previewMode ? value ? /*#__PURE__*/_jsxs(Box, {
      children: [/*#__PURE__*/_jsxs(Box, {
        role: "button",
        "aria-expanded": open,
        onClick: onToggle,
        sx: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          cursor: "pointer",
          userSelect: "none"
        },
        children: [/*#__PURE__*/_jsx(IconButton, {
          size: "small",
          "aria-label": "toggle executive summary",
          sx: {
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 200ms ease"
          },
          children: /*#__PURE__*/_jsx(ExpandMoreIcon, {})
        }), /*#__PURE__*/_jsx(Typography, {
          sx: {
            mb: 0
          },
          variant: "subtitle1",
          fontWeight: 700,
          children: "EXECUTIVE SUMMARY"
        }), /*#__PURE__*/_jsx(IconButton, {
          size: "small",
          "aria-label": "toggle executive summary",
          sx: {
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 200ms ease"
          },
          children: /*#__PURE__*/_jsx(ExpandMoreIcon, {})
        })]
      }), /*#__PURE__*/_jsx(Collapse, {
        in: open,
        timeout: 350,
        children: /*#__PURE__*/_jsx(Paper, {
          variant: "outlined",
          sx: {
            p: 2,
            mt: 2
          },
          children: /*#__PURE__*/_jsx(Typography, {
            variant: "body2",
            children: value
          })
        })
      })]
    }) : null : /*#__PURE__*/_jsx(TextField, {
      label: "Executive Summary",
      multiline: true,
      fullWidth: true,
      minRows: 3,
      value: value,
      onChange: e => onChange(e.target.value)
    })
  });
}

// ─── Create-page draft persistence (shared) ─────────────────────────────────

/**
 * Persists an in-progress create/edit form to localStorage so a page reload
 * doesn't lose work. New submissions save under `baseKey` (so the index
 * "continue draft" card can find them); edits save under `baseKey:edit:<id>`
 * so they never contaminate the new-submission draft. Restores once on mount
 * (unless a navigation prefill should take precedence) and auto-saves on change.
 *
 * `draftRestoredRef` lets the page short-circuit its own edit-load effect on
 * reload so the restored draft (the user's latest edits) isn't clobbered by the
 * pristine item. Call `clearDraft()` on submit/discard.
 */
export function useCreateDraft(opts) {
  const {
    baseKey,
    isEditMode,
    editId,
    meta,
    content,
    organizationId
  } = opts;
  const enabled = opts.enabled ?? true;
  const storageKey = React.useMemo(() => isEditMode && editId ? `${baseKey}:edit:${editId}` : baseKey, [baseKey, isEditMode, editId]);
  const draftRestoredRef = React.useRef(false);
  const [ready, setReady] = React.useState(false);
  // Keep callbacks in refs so they don't re-trigger the effects.
  const applyRef = React.useRef(opts.applyDraft);
  applyRef.current = opts.applyDraft;
  const hasPrefillRef = React.useRef(opts.hasPrefill);
  hasPrefillRef.current = opts.hasPrefill;
  const shouldPersistRef = React.useRef(opts.shouldPersist);
  shouldPersistRef.current = opts.shouldPersist;

  // Restore once the router is ready (so the key is correct).
  React.useEffect(() => {
    if (!enabled) return;
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem(storageKey) : null;
      if (raw && !hasPrefillRef.current()) {
        applyRef.current(JSON.parse(raw));
        draftRestoredRef.current = true;
      }
    } catch {
      /* ignore malformed drafts */
    }
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, storageKey]);

  // Latest snapshot in a ref, so a synchronous flush (on tab hide / reload)
  // can write the most recent edit without relying on a debounced closure.
  const latestRef = React.useRef({
    meta,
    content,
    organizationId
  });
  latestRef.current = {
    meta,
    content,
    organizationId
  };
  const saveTimerRef = React.useRef(null);
  // Once the draft is explicitly cleared (submit/discard) we must never write
  // again — otherwise an in-flight debounce or unload flush could resurrect a
  // just-submitted draft.
  const clearedRef = React.useRef(false);
  const writeNow = React.useCallback(() => {
    if (clearedRef.current || typeof window === "undefined") return;
    try {
      const keep = shouldPersistRef.current ? shouldPersistRef.current() : true;
      if (keep) {
        localStorage.setItem(storageKey, JSON.stringify(latestRef.current));
      } else {
        // Pristine form — don't leave a phantom draft behind.
        localStorage.removeItem(storageKey);
      }
    } catch {
      /* quota / serialization errors are non-fatal */
    }
  }, [storageKey]);

  // Auto-save on change (debounced), once the initial restore has run.
  React.useEffect(() => {
    if (!ready || !enabled || typeof window === "undefined") return;
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      writeNow();
    }, 300);
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [ready, enabled, storageKey, meta, content, organizationId, writeNow]);

  // Flush synchronously when the tab is hidden / unloaded, so an edit made
  // within the 300ms debounce window isn't lost on reload or close.
  React.useEffect(() => {
    if (!ready || !enabled || typeof window === "undefined") return;
    const flush = () => writeNow();
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
    };
  }, [ready, enabled, writeNow]);
  const clearDraft = React.useCallback(() => {
    clearedRef.current = true;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    try {
      if (typeof window !== "undefined") localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
  }, [storageKey]);
  return {
    draftRestoredRef,
    clearDraft
  };
}

// ─── Create-page draft preview card (shared by both index pages) ─────────────

/**
 * Dashed-border "continue draft" card rendered on an index page when a local
 * create-draft exists in storage. Shared by both index pages; the only
 * difference is the untitled-fallback label. Tag chips are derived from the
 * real meta tag fields (regionTags / filterTags / miscTags).
 */
export function CreateDraftCard({
  draft,
  untitledLabel,
  onOpen,
  onClear
}) {
  const theme = useTheme();
  const meta = draft?.meta || {};
  const chips = [...(meta.regionTags || []), ...(meta.filterTags || []), ...(meta.miscTags || [])].slice(0, 2);
  return /*#__PURE__*/_jsxs(Box, {
    sx: {
      p: 3,
      border: `2px dashed ${theme.palette.warning.main}`,
      borderRadius: 2,
      width: "340px",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      background: theme.palette.action.hover,
      cursor: "pointer",
      boxShadow: theme.shadows[2],
      "&:hover": {
        boxShadow: theme.shadows[10]
      }
    },
    onClick: onOpen,
    children: [/*#__PURE__*/_jsx(Typography, {
      variant: "overline",
      color: "warning.main",
      sx: {
        fontWeight: 700
      },
      children: "Draft"
    }), /*#__PURE__*/_jsx(Typography, {
      variant: "h6",
      gutterBottom: true,
      sx: {
        fontWeight: 600
      },
      children: meta.title || untitledLabel
    }), /*#__PURE__*/_jsx(Typography, {
      variant: "body2",
      color: "text.secondary",
      paragraph: true,
      sx: {
        flexGrow: 1
      },
      children: meta.executiveSummary?.substring(0, 140) || "(No executive summary)"
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        display: "flex",
        gap: 1,
        flexWrap: "wrap"
      },
      children: chips.map(t => /*#__PURE__*/_jsx(Chip, {
        label: t,
        size: "small",
        color: "warning",
        variant: "outlined"
      }, t))
    }), /*#__PURE__*/_jsx(Typography, {
      variant: "caption",
      color: "text.secondary",
      sx: {
        mt: 1
      },
      children: "Local draft (not published)"
    }), /*#__PURE__*/_jsx(Chip, {
      label: "Clear Draft",
      size: "small",
      color: "warning",
      onClick: e => {
        e.stopPropagation();
        onClear();
      },
      sx: {
        position: "absolute",
        top: 8,
        right: 8
      }
    })]
  });
}